import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import iconLaptop from "@/assets/revista-star/icons/laptop.webp.asset.json";
import iconAtom from "@/assets/revista-star/icons/atom.webp.asset.json";
import iconCap from "@/assets/revista-star/icons/cap.webp.asset.json";
import iconGroup from "@/assets/revista-star/icons/group.webp.asset.json";
import iconTarget from "@/assets/revista-star/icons/target.webp.asset.json";
import iconCare from "@/assets/revista-star/icons/care.webp.asset.json";
import logoStarCursos from "@/assets/revista/logo-star-full.png.asset.json";
import mockupStarCursos from "@/assets/revista-star/mockup-star-cursos.png.asset.json";
import fisioCertificado from "@/assets/revista-star/fisio-certificado.webp.asset.json";
import { useOfferCheckoutUrl } from "@/lib/checkout/use-offer-checkout-url";

export const STAR_FAQ = [
  { q: "Como iniciar o meu acesso?", a: "Após a realização do pagamento você receberá a confirmação por e-mail e todas as instruções para acesso imediato à plataforma." },
  { q: "O valor é mensal?", a: "Não, R$77 é o pagamento anual da assinatura. Você tem acesso à Star Cursos por 12 meses." },
  { q: "O valor de R$77 também vale para as próximas renovações?", a: "Sim. Ao assinar nesta oferta, você garante o valor de R$77 por ano também nas próximas renovações, enquanto mantiver sua assinatura ativa. Ou seja: no ano seguinte, o valor não volta para R$307." },
  { q: "Tem certificado?", a: "Sim, você poderá emitir certificado para cada curso que concluir, sem custo adicional." },
  { q: "Como saberei dos novos cursos?", a: "Você será notificado por e-mail e pela nossa Comunidade FEO, onde compartilhamos novidades e conteúdos." },
  { q: "Como tirar dúvidas na plataforma?", a: "Em cada videoaula você pode fazer anotações e enviar perguntas. Os professores respondem regularmente." },
  { q: "As aulas são online?", a: "Sim, todas as aulas são gravadas e 100% online." },
];

export type StarOffer = {
  /** Valor antigo riscado, ex.: "R$307" */
  from: string;
  /** Valor da oferta, ex.: "R$77" */
  now: string;
  /** Sufixo do preço, ex.: "/ano" (omita na vitalícia) */
  period?: string;
  /** Rótulo do CTA secundário, ex.: "Assine por R$77/ano" */
  ctaLabel: string;
};

const DEFAULT_OFFER: StarOffer = {
  from: "R$307",
  now: "R$77",
  period: "/ano",
  ctaLabel: "Assine por R$77/ano",
};

export function RevistaStarContent({
  defaultCheckoutUrl,
  confirmationText = "As suas revistas já estão disponíveis no seu e-mail!",
  heroTitle,
  heroSubtitle,
  offer = DEFAULT_OFFER,
  faq = STAR_FAQ,
}: {
  defaultCheckoutUrl: string;
  confirmationText?: string;
  heroTitle?: React.ReactNode;
  heroSubtitle?: React.ReactNode;
  offer?: StarOffer;
  faq?: { q: string; a: string }[];
}) {

  const CHECKOUT_URL = useOfferCheckoutUrl(defaultCheckoutUrl);
  const textRef = useRef<HTMLDivElement>(null);
  const [textHeight, setTextHeight] = useState<number | null>(null);

  useEffect(() => {
    const measure = () => {
      if (textRef.current) setTextHeight(textRef.current.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <main className="min-h-screen bg-[#0a1240] text-white antialiased">
      <div className="bg-[#00b899] text-white">
        <div className="mx-auto max-w-5xl px-4 py-3 text-center text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 whitespace-nowrap">
          <CheckCircle2 className="w-5 h-5" />
          {confirmationText}
        </div>
      </div>

      <section className="relative overflow-hidden bg-gradient-to-b from-[#0a1240] via-[#0d1a56] to-[#081137]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-[#00b899]/25 blur-[160px]" />
          <div className="absolute bottom-0 -left-40 h-[420px] w-[420px] rounded-full bg-[#00c9d6]/15 blur-[140px]" />
          <div className="absolute bottom-20 -right-40 h-[420px] w-[420px] rounded-full bg-[#6ee7c7]/15 blur-[140px]" />
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)", backgroundSize: "34px 34px" }} />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-20 sm:pt-28 lg:pt-32 pb-0">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-[34px] leading-[1.05] sm:text-5xl lg:text-[56px] font-black tracking-tight">
                {heroTitle ?? (
                  <>
                    Você ganhou um cupom para assinar a{" "}
                    <span className="bg-gradient-to-r from-[#6ee7c7] via-[#00c9d6] to-[#00b899] bg-clip-text text-transparent">
                      Star Cursos por apenas R$77/ano
                    </span>
                  </>
                )}
              </h1>

              <p className="mt-6 text-base sm:text-lg text-white/70">
                {heroSubtitle ?? (
                  <>
                    Esse é o{" "}
                    <span className="text-white font-semibold underline decoration-[#00b899]/70 underline-offset-4">menor valor</span>{" "}
                    da nossa história.
                  </>
                )}
              </p>

              <div className="mt-6 flex flex-col items-center lg:items-start gap-6 max-w-2xl mx-auto lg:mx-0">
                <a href={CHECKOUT_URL} target="_blank" rel="noopener"
                  className="group inline-flex items-center justify-center gap-3 rounded-xl bg-[#00b899] hover:bg-[#00a085] text-white font-black text-base sm:text-lg px-8 py-4 shadow-xl shadow-[#00b899]/30 hover:-translate-y-0.5 transition">
                  QUERO ASSINAR AGORA
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </a>

                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] backdrop-blur px-5 py-2">
                  <span className="text-sm text-white/60">de <span className="line-through">{offer.from}</span> por</span>
                  <span className="text-3xl font-black text-white">{offer.now}</span>
                  {offer.period && <span className="text-sm text-white/60">{offer.period}</span>}
                </div>
              </div>

            </div>

            <div className="relative flex items-center justify-center px-2 sm:px-0">
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-[80%] w-[90%] rounded-full bg-[#00b899]/25 blur-[80px]" />
              </div>
              <img src={mockupStarCursos.url} alt="Mockup da plataforma Star Cursos"
                className="relative z-10 w-full max-w-xl sm:max-w-2xl lg:max-w-2xl h-auto drop-shadow-[0_40px_80px_rgba(0,184,153,0.4)]" loading="lazy" />
            </div>
          </div>
        </div>

        <div className="relative mt-16 sm:mt-20 lg:mt-24 border-t border-white/10 bg-[#081137]/60 backdrop-blur">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              <Stat number="+15 mil" label="alunos" />
              <Stat number="+50" label="professores" />
              <Stat number="+350h" label="de conteúdo" />
              <Stat number="+120" label="cursos" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-white text-slate-900 px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl grid gap-10 lg:gap-12 lg:grid-cols-2 lg:items-start">
          <div className="order-2 lg:order-1 relative flex items-start justify-center" style={{ height: textHeight ? `${textHeight}px` : "auto" }}>
            <img src={fisioCertificado.url} alt="Fisioterapeuta com certificado Star Cursos" className="h-full w-auto max-w-full object-contain" loading="lazy" />
          </div>
          <div ref={textRef} className="order-1 lg:order-2 flex flex-col text-center lg:text-left">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#00b899]">UMA NOVA FORMA DE APRENDER</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight text-[#0d1a56]">
                Domine a fisioterapia musculoesquelética com a{" "}
                <span className="bg-gradient-to-r from-[#00b899] to-[#0d1a56] bg-clip-text text-transparent">maior biblioteca de cursos da área.</span>
              </h2>
            </div>
            <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
              <p>A <strong className="text-slate-900">Star Cursos</strong> é a maior biblioteca de cursos online em fisioterapia musculoesquelética. São <strong className="text-slate-900">+120 cursos com certificação</strong> para você acessar quando, onde e como quiser.</p>
              <p>Para você que quer resultados <strong className="text-slate-900">100% baseados em evidências científicas</strong>, ao lado de grandes nomes da fisioterapia.</p>
              <a href={CHECKOUT_URL} target="_blank" rel="noopener"
                className="group mt-6 inline-flex items-center justify-center gap-3 rounded-2xl bg-[#00b899] hover:bg-[#00a085] text-white font-black text-base sm:text-lg px-8 py-5 shadow-2xl shadow-[#00b899]/40 hover:-translate-y-0.5 transition w-full sm:w-auto">
                {offer.ctaLabel}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f1f1f1] text-slate-900 px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl space-y-14">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#00b899]">O que você recebe</span>
            <h2 className="mt-4 text-2xl sm:text-4xl font-black tracking-tight text-[#0d1a56]">
              Seja para estudar um caso clínico, se atualizar ou investir no seu crescimento: a Star está com você.
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Benefit icon={iconLaptop.url} title="+120 cursos" text="Biblioteca viva com atualizações mensais. Acesso imediato, estude no seu tempo." />
            <Benefit icon={iconAtom.url} title="Baseado em evidências" text="Todos os cursos 100% baseados em evidências científicas." />
            <Benefit icon={iconCap.url} title="Professores renomados" text="Aprenda com profissionais reconhecidos, em aulas práticas e teóricas." />
            <Benefit icon={iconGroup.url} title="Acesso à comunidade" text="Faça parte de uma rede com mais de 15 mil fisioterapeutas conectados." />
            <Benefit icon={iconTarget.url} title="Praticidade real" text="Aulas objetivas que acompanham a sua rotina, com foco no seu desenvolvimento." />
            <Benefit icon={iconCare.url} title="Exclusiva para fisios" text="Para fisioterapeutas que atuam com dor, ortopedia, esporte e saúde musculoesquelética." />
          </div>

          <div className="text-center">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-[#0d1a56] hover:bg-[#00b899] text-white font-black px-9 py-4 transition">
              ASSINE AGORA
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white text-slate-900 px-4 sm:px-6 py-20 sm:py-24">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#00b899]">Dúvidas frequentes</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#0d1a56]">Ficou com alguma dúvida?</h2>
          </div>
          <div className="space-y-3">
            {faq.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-slate-200 bg-white p-5 open:shadow-md transition">
                <summary className="cursor-pointer list-none font-bold text-slate-900 flex items-center justify-between gap-3">
                  {f.q}
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-slate-100 text-[#00b899] group-open:bg-[#00b899] group-open:text-white group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white text-slate-900 px-4 sm:px-6 py-12 sm:py-16 border-t border-slate-100">
        <div className="mx-auto max-w-3xl text-center">
          <img src={logoStarCursos.url} alt="Star Cursos" className="mx-auto h-20 sm:h-24 w-auto" loading="lazy" />
        </div>
      </section>

      <footer className="bg-[#050a2a] text-white/50 text-center text-sm py-8 px-4">
        © {new Date().getFullYear()} Portal Fisio em Ortopedia · Todos os direitos reservados
      </footer>
    </main>
  );
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-2xl sm:text-3xl font-black text-[#6ee7c7] leading-none">{number}</div>
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/60 mt-1.5">{label}</div>
    </div>
  );
}

function Benefit({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="text-center sm:text-left">
      <div className="w-16 h-16 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center mx-auto sm:mx-0">
        <img src={icon} alt="" className="w-10 h-10 object-contain" loading="lazy" />
      </div>
      <h3 className="mt-5 text-lg font-black text-[#0d1a56]">{title}</h3>
      <p className="mt-2 text-slate-600 text-sm leading-relaxed">{text}</p>
    </div>
  );
}
